import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import type { Direction } from "../direction";
import { cycleDirection, directionDelta, directionFromVector } from "../direction";
import { Explosion } from "../effects/explosion";
import type { PhysicalThing } from "../physicalThing";
import { randomInt } from "../random";
import { deserializeXY } from "../serialization";
import type { Thing } from "../thing";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { XY } from "../xy";
import { HeavyRobotPart } from "./heavyRobotPart";

export class HeavyRobot extends Mob {
	static key = "HeavyRobot";
	private parts: HeavyRobotPart[] = [];
	private turnsSinceLastMove: number = 0;
	private static readonly moveEveryXTurns = 0;

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(HeavyRobot.key);
	}

	constructor(xy: XY) {
		super(xy);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.hp = 60;
		this.maxHp = 60;
	}

	addPart(part: HeavyRobotPart): void {
		this.parts.push(part);
		part.setMaster(this);
	}

	getParts(): HeavyRobotPart[] {
		return this.parts;
	}

	processMobAction(_board: Board): void {
		if (this.turnsSinceLastMove >= HeavyRobot.moveEveryXTurns) {
			if (this.hp < 60) {
				// Run away AI when damaged
				const direction = this.getDirectionToPlayer();
				if (direction !== null) {
					const reversed = cycleDirection(direction, 4); // reverse
					if (this.canMoveToDirection(reversed)) {
						this.doMove(reversed);
					}
				}
			}
			// At full HP: idle (original code has this commented out)
			this.turnsSinceLastMove = 0;
		} else {
			this.turnsSinceLastMove++;
		}
	}

	private canMoveToDirection(d: Direction): boolean {
		const currentCoords = new Set<string>();
		currentCoords.add(`${this.xy.x},${this.xy.y}`);
		for (const part of this.parts) {
			if (part.active) {
				currentCoords.add(`${part.xy.x},${part.xy.y}`);
			}
		}

		const delta = directionDelta(d);

		// Check core
		if (!this.isNewCoordOk(this.xy.add(delta), currentCoords)) return false;

		// Check parts
		for (const part of this.parts) {
			if (part.active) {
				if (!this.isNewCoordOk(part.xy.add(delta), currentCoords)) return false;
			}
		}

		return true;
	}

	private isNewCoordOk(xy: XY, whiteListCoords: Set<string>): boolean {
		if (whiteListCoords.has(`${xy.x},${xy.y}`)) return true;
		return this.board!.isInBounds(xy) && this.board!.isPassable(xy) && !this.board!.hasMob(xy);
	}

	private doMove(d: Direction): void {
		const delta = directionDelta(d);
		this.xy = this.xy.add(delta);

		for (const part of this.parts) {
			if (part.active) {
				part.masterSetCoords = part.xy.add(delta);
			}
		}
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		this.board!.addEffect(new Explosion(this.xy));
		for (const part of this.parts) {
			if (part.active) {
				part.kill();
				this.board!.addEffect(new Explosion(part.xy));
			}
		}
	}

	private getDirectionToPlayer(): Direction | null {
		if (!this.board!.player) return null;
		const target = this.board!.player.xy;

		if (target.equals(this.xy)) return null;

		const deltax = target.x - this.xy.x;
		const deltay = target.y - this.xy.y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));

		let direction = directionFromVector(vector);
		if (!direction) return null;

		direction = cycleDirection(direction, randomInt(-1, 1));
		return direction;
	}

	generateCorpse(): PhysicalThing | null {
		return null; // Explodes — no corpse
	}

	static create(board: Board, coreXY: XY, partOffsets: XY[]): HeavyRobot | null {
		const robot = new HeavyRobot(coreXY);
		board.addThing(robot);
		for (const offset of partOffsets) {
			const partXY = coreXY.add(offset);
			const part = new HeavyRobotPart(partXY);
			board.addThing(part);
			robot.addPart(part);
		}
		return robot;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			turnsSinceLastMove: this.turnsSinceLastMove,
		};
	}

	static deserialize(data: any): HeavyRobot {
		const r = new HeavyRobot(deserializeXY(data.xy));
		r.restoreMobFields(data);
		r.turnsSinceLastMove = data.turnsSinceLastMove ?? 0;
		return r;
	}
}
